import { useMemo, type ReactNode } from 'react'
import isNumber from 'lodash/isNumber'
import toString from 'lodash/toString'
import type { OrderFieldChange, OrderForm, OrderFormErrorMap } from '@klear/order/wire.client'
import { useCustomers } from '@klear/customer-ui/use-customers'
import { Combobox } from '@klear/ui/components/combobox'
import { Label } from '@klear/ui/components/label'
import { Textarea } from '@klear/ui/components/textarea'

interface OrderFieldsProps {
  traderId: number
  values: OrderForm
  errors: OrderFormErrorMap
  onFieldChange: OrderFieldChange
  createCustomerAction?: ReactNode
}

function FieldError({ message }: { message?: string }) {
  return message ? <p className="text-sm text-destructive">{message}</p> : null
}

export function OrderFields({ traderId, values, errors, onFieldChange, createCustomerAction }: OrderFieldsProps) {
  const { customers } = useCustomers(traderId)

  const customerOptions = useMemo(
    () =>
      customers
        .filter((customer) => isNumber(customer.id))
        .map((customer) => ({
          value: toString(customer.id),
          label: customer.name
        })),
    [customers]
  )

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium">Customer</Label>
        <Combobox
          value={values.customerId}
          options={customerOptions}
          placeholder="Search customers…"
          triggerLabel="Select customer…"
          onChange={(v) => onFieldChange('customerId', v)}
          error={errors.customerId ? { message: errors.customerId } : undefined}
        />
        <FieldError message={errors.customerId} />
        {createCustomerAction}
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Description</Label>
        <Textarea value={values.description} onChange={(e) => onFieldChange('description', e.target.value)} />
      </div>
    </div>
  )
}
